import React, { useEffect, useMemo, useState } from "react";
import Pagination from "../../components/Pagination";

function buildFilterSummary(config, selectedContent, selectedType) {
  if (!selectedContent.length && !selectedType.length) {
    return "";
  }

  let summary = " in";

  if (selectedContent.length) {
    summary += ` ${config.byContentLabel}: ${selectedContent.join(", ")}`;
  }

  if (selectedType.length) {
    summary += selectedContent.length
      ? ` and ${config.byContentTypeLabel}: ${selectedType.join(", ")}`
      : ` ${config.byContentTypeLabel}: ${selectedType.join(", ")}`;
  }

  return summary;
}

function scrollToMain() {
  if (window.jQuery) {
    const main = window.jQuery(".main");

    if (main.length) {
      window.jQuery("html, body").animate({ scrollTop: main.offset().top }, 300);
      return;
    }
  }

  const main = document.querySelector(".main");

  if (main) {
    main.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

export default function SearchPageIsland({ config }) {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showFilter, setShowFilter] = useState(false);
  const [error, setError] = useState("");
  const [searchData, setSearchData] = useState([]);
  const [selectedContent, setSelectedContent] = useState(config.initialSelectedContent || []);
  const [selectedType, setSelectedType] = useState(config.initialSelectedType || []);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(config.pageSize);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const showToggle = windowWidth > 768 || showFilter;

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    let isActive = true;

    async function loadResults() {
      document.documentElement.classList.add("loading");
      setLoading(true);
      setError("");

      try {
        const response = await fetch(config.searchApiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
          body: JSON.stringify({
            Customer: "",
            Category: "",
            ResourceType: selectedType.join(";").replace(/ /g, ""),
            OrderBy: "",
            SearchTerm: config.initialSearchTerm,
            PageNumber: pageNumber,
            Phase: "",
            Status: "",
            Topic: "",
            Release: "",
            ContentType: selectedContent.join(";").replace(/ /g, ""),
            NodeId: "",
          }),
        });

        if (!response.ok) {
          throw new Error(`Search request failed with ${response.status}`);
        }

        const result = await response.json();

        if (!isActive) {
          return;
        }

        setSearchData(Array.isArray(result.Results) ? result.Results : []);
        setTotal(Number(result.TotalNumberOfRecords) || 0);
        setPageSize(Number(result.PageSize) || config.pageSize);
        setPageNumber(Number(result.PageNumber) || 1);
      } catch (requestError) {
        if (!isActive) {
          return;
        }

        setSearchData([]);
        setTotal(0);
        setError(requestError?.message || "Unable to load search results.");
      } finally {
        if (!isActive) {
          return;
        }

        setLoading(false);
        scrollToMain();
        document.documentElement.classList.remove("loading");
      }
    }

    loadResults();

    return () => {
      isActive = false;
      document.documentElement.classList.remove("loading");
    };
  }, [config.initialSearchTerm, config.pageSize, config.searchApiUrl, pageNumber, selectedContent, selectedType]);

  const resultsText = useMemo(() => {
    if (loading) {
      return config.loadingText;
    }

    return `${total} ${config.resultsLabel}${
      config.initialSearchTerm
        ? ` for <span class='text-bolder'>'${config.initialSearchTerm}'</span>`
        : ""
    }${buildFilterSummary(config, selectedContent, selectedType)}`;
  }, [config, loading, selectedContent, selectedType, total]);

  const onContentChange = (value, checked) => {
    setSelectedContent((currentItems) => {
      const nextItems = checked
        ? [...currentItems, value]
        : currentItems.filter((item) => item !== value);

      return nextItems;
    });
    setPageNumber(1);
  };

  const onTypeChange = (value, checked) => {
    setSelectedType((currentItems) => {
      const nextItems = checked
        ? [...currentItems, value]
        : currentItems.filter((item) => item !== value);

      return nextItems;
    });
    setPageNumber(1);
  };

  return (
    <>
      {error ? <div className="alert alert-danger">{error}</div> : null}
      <div className="row">
        <div className="col-md-3 mb-4 order-md-2">
          <button
            className="btn btn-primary w-100 d-md-none mb-3"
            type="button"
            onClick={() => setShowFilter((currentValue) => !currentValue)}
          >
            {showToggle ? config.hideFilterLabel : config.showFilterLabel}
          </button>
          <div className="row" style={{ display: showToggle ? "flex" : "none" }}>
            <div className="col-md-12 col-sm-6">
              <h3>{config.byContentLabel}</h3>
              <ul className="side-nav">
                {config.contentFilter
                  .filter((item) => item && item.length > 0)
                  .map((item, index) => {
                    const id = `contentCheckbox${index}`;

                    return (
                      <li className="side-nav-item" key={id}>
                        <div className="form-group input-checkbox my-2">
                          <input
                            id={id}
                            type="checkbox"
                            value={item}
                            checked={selectedContent.includes(item)}
                            onChange={(event) => onContentChange(item, event.target.checked)}
                          />
                          <label htmlFor={id}>{item}</label>
                        </div>
                      </li>
                    );
                  })}
              </ul>
            </div>
            <div className="col-md-12 col-sm-6">
              <h3 className="mt-4 mt-sm-0 mt-md-5">{config.byContentTypeLabel}</h3>
              <ul className="side-nav">
                {config.typeFilter
                  .filter((item) => item && item.length > 0)
                  .map((item, index) => {
                    const id = `typeCheckbox${index}`;

                    return (
                      <li className="side-nav-item" key={id}>
                        <div className="form-group input-checkbox my-2">
                          <input
                            id={id}
                            type="checkbox"
                            value={item}
                            checked={selectedType.includes(item)}
                            onChange={(event) => onTypeChange(item, event.target.checked)}
                          />
                          <label htmlFor={id}>{item}</label>
                        </div>
                      </li>
                    );
                  })}
              </ul>
            </div>
          </div>
        </div>
        <div className="col-md-9 order-md-1">
          <h2 dangerouslySetInnerHTML={{ __html: resultsText }} />
          <div className="search-results-list">
            {searchData.map((item, index) => (
              <div className="search-results-item" key={`${item.Url || item.Title}-${index}`}>
                <a href={item.Url} className="link-hover">
                  <h3>{item.Title}</h3>
                </a>
                <div dangerouslySetInnerHTML={{ __html: item.Description }} />
              </div>
            ))}
          </div>
        </div>
      </div>
      {total > pageSize ? (
        <Pagination
          currentPage={pageNumber}
          pageSize={pageSize}
          totalItems={total}
          onPageChange={setPageNumber}
        />
      ) : null}
    </>
  );
}
